import { Button, Form, Image, Input, Skeleton, Space, Tooltip, Select } from "antd";

import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

// import states from "states-us"; // Commented out: US states list
// eslint-disable-next-line import/no-extraneous-dependencies
import { Country, State } from "country-state-city";
import axios from "axios";

import { GoQuestion } from "react-icons/go";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./phone.css";
import { useHistory, useLocation } from "react-router-dom";
import ImageUpload from "../../../cleaner/components/ImageUpload";
import confirmModal from "../../../../components/custom_antd/confirmModal";
import apiClient from "../../../../helpers/apiClient";
import ImageCompressor from "../../../../utilities/ImageCompressor";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import { useToast } from "../../../../contexts/ToastContext";
import SettingsEditLink from "../SettingsEditLink";
import SettingsSection, { SettingsInfoField, formatSettingsPhone } from "../SettingsSection";

const FORM_GRID_CLASS = "grid grid-cols-1 gap-x-6 md:grid-cols-2";
const DETAIL_GRID_CLASS = "grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2";

const initialValues = {
    name: "",
    phone: "",
    email: "",
    website: "",
    address: {
        city: "",
        state: "",
        country: "",
        zip: "",
        address: "",
    },
};
const BasicCompInfo = ({
    companyData = initialValues,
    fetchCompanyData,
    initialLoading,
    hideActionBar = false,
    onRegisterHeaderActions,
}) => {
    const { t } = useTranslation("settings");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const query = useLocation().search;
    const history = useHistory();
    const [isEditing, setIsEditing] = useState(false);
    const [darkModeLogo, setDarkModeLogo] = useState([]);
    const [lightModeLogo, setLightModeLogo] = useState([]);
    const toast = useToast();

    const [form] = Form.useForm();
    const watchedCountryIsoCode = Form.useWatch(["address", "country"], form);

    const removeEditFromURL = useCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete("edit");
        const newUrl = urlParams.toString() ? `?${urlParams.toString()}` : window.location.pathname;
        history.push(newUrl, { replace: true });
    }, [history]);

    const handleStartEdit = useCallback(() => {
        setIsEditing(true);
    }, []);

    const handleSaveClick = useCallback(() => {
        form.submit();
    }, [form]);

    useEffect(() => {
        if (query.includes("edit")) {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [query]);

    useEffect(() => {
        form.setFieldsValue(companyData);

        if (companyData?.logo?.dark) {
            setDarkModeLogo([
                {
                    uid: "-1",
                    name: "dark-logo.png",
                    url: companyData.logo.dark,
                },
            ]);
        }
        if (companyData?.logo?.light) {
            setLightModeLogo([
                {
                    uid: "1",
                    name: "light-logo.png",
                    url: companyData.logo.light,
                },
            ]);
        }
        // Normalize country to ISO code for the Select value
        if (companyData?.address?.country) {
            const matched = Country.getAllCountries().find(
                (c) => c.name === companyData.address.country || c.isoCode === companyData.address.country,
            );
            if (matched && matched.isoCode !== companyData.address.country) {
                form.setFieldsValue({ address: { country: matched.isoCode } });
            }
        }
    }, [companyData, form]);

    const onFinish = async (values) => {
        setIsSubmitting(true);
        try {
            // Convert country ISO code to country name before saving
            const countryName =
                Country.getAllCountries().find((c) => c.isoCode === values?.address?.country)?.name ||
                values?.address?.country ||
                "";
            const valuesToSave = {
                ...values,
                address: {
                    ...values.address,
                    country: countryName,
                },
            };
            const logo = {
                dark: companyData.logo?.dark || "",
                light: companyData.logo?.light || "",
            };
            if (darkModeLogo.length > 0 && darkModeLogo[0].originFileObj) {
                const compressed = await ImageCompressor(darkModeLogo[0].originFileObj, 2);

                const imageData = new FormData();
                imageData.append("image", compressed);
                imageData.append("imagePath", "Logos");

                const { data } = await axios.post(
                    `${process.env.REACT_APP_SOFTCAR_OLD_API}/uploadSingleImage`,
                    imageData,
                );

                logo.dark = data.url;
            } else if (darkModeLogo.length === 0) {
                delete logo.dark;
            }

            if (lightModeLogo.length > 0 && lightModeLogo[0].originFileObj) {
                const compressed = await ImageCompressor(lightModeLogo[0].originFileObj, 2);
                const imageData = new FormData();
                imageData.append("image", compressed);
                imageData.append("imagePath", "Logos");

                const { data } = await axios.post(
                    `${process.env.REACT_APP_SOFTCAR_OLD_API}/uploadSingleImage`,
                    imageData,
                );

                logo.light = data.url;
            } else if (lightModeLogo.length === 0) {
                delete logo.light;
            }

            const resp = await apiClient.patch("/api/company/myCompany", { ...valuesToSave, logo });
            if (resp?.data?.success) {
                toast.success({
                    message: t("successfullyUpdatedInformation"),
                });
                setIsSubmitting(false);
                await fetchCompanyData();
                setIsEditing(false);
                removeEditFromURL(); // Remove edit query parameter
            } else {
                throw new Error("no success message");
            }
        } catch (_error) {
            setIsSubmitting(false);
            toast.error({ message: t("failedToUpdateInformation") });
            // console.error(error?.response || error);
        }
    };

    const handleCancelClick = useCallback(async () => {
        const confirmed = await confirmModal({
            title: t("discardChangesConfirmation"),
            okText: t("discard"),
            cancelText: t("cancel"),
            danger: true,
        });

        if (confirmed) {
            form.setFieldsValue(companyData);
            setIsEditing(false);
            removeEditFromURL();
        }
    }, [companyData, form, removeEditFromURL, t]);

    const headerActions = useMemo(() => {
        if (!hideActionBar) {
            return null;
        }

        if (initialLoading) {
            return <Skeleton.Button style={{ width: "132px" }} />;
        }

        if (isEditing) {
            return (
                <Space className="flex w-full lg:w-auto [&_.ant-space-item]:flex-1 lg:[&_.ant-space-item]:flex-none">
                    <Button
                        className="!w-full lg:!w-auto"
                        shape="round"
                        onClick={handleCancelClick}
                        disabled={isSubmitting}>
                        {t("cancel")}
                    </Button>
                    <Button
                        className="!w-full lg:!w-auto"
                        type="primary"
                        disabled={isSubmitting}
                        onClick={handleSaveClick}
                        htmlType="submit"
                        shape="round">
                        {isSubmitting ? t("saving") : t("save")}
                    </Button>
                </Space>
            );
        }

        return <SettingsEditLink onClick={handleStartEdit} />;
    }, [
        handleCancelClick,
        handleSaveClick,
        handleStartEdit,
        hideActionBar,
        initialLoading,
        isEditing,
        isSubmitting,
        t,
    ]);

    useEffect(() => {
        if (hideActionBar && onRegisterHeaderActions) {
            onRegisterHeaderActions(headerActions);
        }
    }, [headerActions, hideActionBar, onRegisterHeaderActions]);

    const logoTiles = [
        { key: "light", url: companyData?.logo?.light, label: t("lightModeLogo"), tileClass: "bg-[#F5F5FA]" },
        { key: "dark", url: companyData?.logo?.dark, label: t("darkModeLogo"), tileClass: "bg-[#1F1F2E]" },
    ];
    const hasLogo = logoTiles.some((tile) => tile.url);

    return (
        <Form
            className="basic-comp-form flex flex-col gap-5"
            form={form}
            layout="vertical"
            colon={false}
            onFinish={onFinish}
            autoComplete="off"
            requiredMark={customizeRequiredMark}
            initialValues={companyData}>
            {!hideActionBar ? (
                <div className="flex h-10 items-center justify-between gap-20 2xl:gap-48">
                    <h4 className="text-lg font-semibold">{t("companyInformation")}</h4>
                    <div>{headerActions}</div>
                </div>
            ) : null}
            {initialLoading ? (
                <>
                    <SettingsSection>
                        <Skeleton active paragraph={{ rows: 2 }} />
                    </SettingsSection>
                    <SettingsSection>
                        <Skeleton active paragraph={{ rows: 4 }} />
                    </SettingsSection>
                </>
            ) : null}
            {initialLoading ? null : isEditing ? (
                <>
                    <SettingsSection
                        title={
                            <span className="inline-flex items-center gap-1">
                                {t("logo")}
                                <Tooltip title={t("logoHelp")}>
                                    <GoQuestion color="gray" size={14} className="cursor-help" />
                                </Tooltip>
                            </span>
                        }>
                        <div className="flex flex-wrap gap-6">
                            <div className="flex flex-col items-center gap-2">
                                <ImageUpload fileList={lightModeLogo} maxCount={1} handleFileChange={setLightModeLogo} />
                                <p className="text-[13px] text-[#787882]">{t("lightModeLogo")}</p>
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                <ImageUpload fileList={darkModeLogo} maxCount={1} handleFileChange={setDarkModeLogo} />
                                <p className="text-[13px] text-[#787882]">{t("darkModeLogo")}</p>
                            </div>
                        </div>
                    </SettingsSection>

                    <SettingsSection title={t("basicInformation")} bodyClassName="!pb-1">
                        <div className={FORM_GRID_CLASS}>
                            <Form.Item name="name" label={t("name")} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="phone"
                                label={t("phone")}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please input your phone number!",
                                    },
                                    {
                                        min: 10,
                                        message: "Please input valid phone number!",
                                    },
                                ]}>
                                <PhoneInput country={"us"} enableSearch={true} />
                            </Form.Item>

                            <Form.Item name="email" label={t("email")} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="website" label={t("website")}>
                                <Input />
                            </Form.Item>
                        </div>
                    </SettingsSection>

                    <SettingsSection title={t("address")} bodyClassName="!pb-1">
                        <div className={FORM_GRID_CLASS}>
                            <Form.Item name={["address", "address"]} label={t("address")} className="md:col-span-2">
                                <Input />
                            </Form.Item>

                            <Form.Item name={["address", "city"]} label={t("city")}>
                                <Input />
                            </Form.Item>

                            <Form.Item name={["address", "zip"]} label={t("zipCode")}>
                                <Input />
                            </Form.Item>

                            <Form.Item name={["address", "country"]} label={t("country")} rules={[{ required: true }]}>
                                <Select
                                    placeholder={t("pleaseEnterCountryName")}
                                    options={Country.getAllCountries().map((c) => ({ value: c.isoCode, label: c.name }))}
                                    optionFilterProp="label"
                                    showSearch
                                    onChange={() => {
                                        // Clear state when country changes so states list will refresh
                                        const currentAddress = form.getFieldValue("address") || {};
                                        form.setFieldsValue({ address: { ...currentAddress, state: "" } });
                                    }}
                                />
                            </Form.Item>

                            <Form.Item name={["address", "state"]} label={t("state")} rules={[{ required: true }]}>
                                <Select
                                    placeholder={t("pleaseEnterStateName")}
                                    options={(watchedCountryIsoCode
                                        ? State.getStatesOfCountry(watchedCountryIsoCode)
                                        : []
                                    ).map((s) => ({ value: s.name, label: s.name }))}
                                    optionFilterProp="label"
                                    disabled={!watchedCountryIsoCode}
                                    showSearch
                                />
                            </Form.Item>
                        </div>
                    </SettingsSection>
                </>
            ) : (
                <>
                    <SettingsSection title={t("logo")}>
                        {hasLogo ? (
                            <div className="flex flex-wrap gap-6">
                                {logoTiles
                                    .filter((tile) => tile.url)
                                    .map((tile) => (
                                        <div key={tile.key} className="flex flex-col items-center gap-2">
                                            <div
                                                className={`flex h-24 w-24 items-center justify-center rounded-xl p-2 ${tile.tileClass}`}>
                                                <Image
                                                    src={tile.url}
                                                    alt={tile.label}
                                                    className="max-h-full max-w-full object-contain"
                                                    preview={false}
                                                />
                                            </div>
                                            <p className="text-[13px] text-[#787882]">{tile.label}</p>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <p className="text-[14px] text-[#9B9BA5]">{t("noLogoUploaded", "No logo uploaded yet")}</p>
                        )}
                    </SettingsSection>

                    <SettingsSection title={t("basicInformation")}>
                        <dl className={DETAIL_GRID_CLASS}>
                            <SettingsInfoField label={t("name")}>{companyData?.name}</SettingsInfoField>
                            <SettingsInfoField label={t("phone")}>{formatSettingsPhone(companyData?.phone)}</SettingsInfoField>
                            <SettingsInfoField label={t("email")}>{companyData?.email}</SettingsInfoField>
                            <SettingsInfoField label={t("website")}>
                                {companyData?.website ? (
                                    <a
                                        href={companyData.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#5D5DF6] hover:underline">
                                        {companyData.website}
                                    </a>
                                ) : null}
                            </SettingsInfoField>
                        </dl>
                    </SettingsSection>

                    <SettingsSection title={t("address")}>
                        <dl className={DETAIL_GRID_CLASS}>
                            <SettingsInfoField label={t("address")} className="md:col-span-2">
                                {companyData?.address?.address}
                            </SettingsInfoField>
                            <SettingsInfoField label={t("city")}>{companyData?.address?.city}</SettingsInfoField>
                            <SettingsInfoField label={t("zipCode")}>{companyData?.address?.zip}</SettingsInfoField>
                            <SettingsInfoField label={t("country")}>{companyData?.address?.country}</SettingsInfoField>
                            <SettingsInfoField label={t("state")}>{companyData?.address?.state}</SettingsInfoField>
                        </dl>
                    </SettingsSection>
                </>
            )}
        </Form>
    );
};
BasicCompInfo.propTypes = {
    companyData: PropTypes.object,
    fetchCompanyData: PropTypes.func,
    initialLoading: PropTypes.bool,
    hideActionBar: PropTypes.bool,
    onRegisterHeaderActions: PropTypes.func,
};
export default BasicCompInfo;
